import { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';

// Utils
import { Storage } from '../../../utils/Storage';

// Hooks and services
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { makeContractOverviewColumns } from '../../../utils/columns/makeContractOverviewColumns';
import { deleteContract, getContracts } from '../../../services/contract.service';

// Types
import { upsertContractModel } from '../../../requestModels/upsertContractModel';

// Commmon
import SearchBar from '../../common/SearchBar/SearchBar';
import { useStandardTable as useTable, Table } from '../../common/table/index';
import { CONFIRM_DIALOG_CONTRACT_DELETE, SearchBarFilterTypes } from '../../../constants/Constants';

// Components
import ContractForm from './ContractForm';

const ContractOverview = () => {
  const [paramFilter, setParamFilter] = useState<any>({});
  const [isResetting, setIsResetting] = useState(false);
  const [currentContract, setCurrentContract] = useState<upsertContractModel | undefined>();
  const [isContractFormOpen, setIsContractFormOpen] = useState(false);

  const deleteDialog = useConfirmDialog({
    title: CONFIRM_DIALOG_CONTRACT_DELETE.title,
    cancelButtonTitle: CONFIRM_DIALOG_CONTRACT_DELETE.cancelButtonTitle,
    okButtonTitle: CONFIRM_DIALOG_CONTRACT_DELETE.okButtonTitle,
  });

  const refreshTable = () => {
    setParamFilter({ ...paramFilter, changedAt: new Date() });
  };

  const setContractFormDialogOpen = (isOpen: boolean) => {
    setIsContractFormOpen(isOpen);
    if (!isOpen) {
      setCurrentContract(undefined);
      refreshTable();
    }
  };

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const handleReset = () => {
    setIsResetting(true);
    setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.CONTRACTS, { filters: [] });
  };

  const onEdit = (contractDetails: any) => {
    setCurrentContract({
      name: contractDetails.name,
      description: contractDetails.description,
      startDate: dayjs(contractDetails.startDate),
      endDate: contractDetails.endDate ? dayjs(contractDetails.endDate) : undefined,
      id: contractDetails.id,
    });
    setContractFormDialogOpen(true);
  };

  const onDelete = async (id: string) => {
    const { accepted } = await deleteDialog.confirm({});
    if (accepted) {
      await deleteContract(id);
      refreshTable();
    }
  };

  const columns = useMemo(() => makeContractOverviewColumns(onEdit, onDelete), []);

  const table = useTable<any>({
    columns,
    fetchAsync: getContracts,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const onSearchChange = (filters: any) => {
    setParamFilter({ filters: filters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.CONTRACTS, { isMoreFilter: true, filters: filters });
  };

  return (
    <>
      {deleteDialog.renderDialog()}

      <ContractForm model={currentContract} isOpen={isContractFormOpen} setIsOpen={setContractFormDialogOpen} />

      <SearchBar pageKey={SearchBarFilterTypes.CONTRACTS} onSearch={onSearchChange} onReset={handleReset} columns={columns} allowSearchAll={true} />
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default ContractOverview;
